import axios from "axios";
import { fullAPIURL } from "../utils/requests";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import { isProd } from "../env";

const DEBUG_NUDGE_KEY = "pp_debug_nudges";

function getDebugNudgeKeys(): string[] {
  try {
    const value = localStorage.getItem(DEBUG_NUDGE_KEY);
    return value ? JSON.parse(value) : [];
  } catch (e) {
    console.warn("Failed to parse debug nudges from localStorage:", e);
    return [];
  }
}

if (!isProd()) {
  (window as any).nudge = {
    help: () => {
      console.log("\n");
      console.log(
        "%c🔧 PushPress Nudge Debugger 5000 🔧",
        "color: #1976D2; font-size: 14px; font-weight: bold; text-decoration: underline;",
      );
      console.log("Use this tool to force a nudge to be shown");
      console.log("This will help you sanity check your nudge placement, etc.");
      console.log(
        "Even if your current gym would not be targeted by the nudge",
      );
      console.log("\n");
      console.log(
        "%cnudge.add(key)%c - Add a nudge to the debugger - example: %cnudge.add('client.upgrade.free_to_pro')",
        "color: #4CAF50; font-weight: bold",
        "color: inherit",
        "color: #2196F3; font-style: italic",
      );
      console.log(
        "%cnudge.remove(key)%c - Remove a nudge from the debugger - example: %cnudge.remove('client.upgrade.free_to_pro')",
        "color: #F44336; font-weight: bold",
        "color: inherit",
        "color: #2196F3; font-style: italic",
      );
      console.log(
        "%cnudge.list()%c - List all nudges in the debugger - example: %cnudge.list()",
        "color: #FF9800; font-weight: bold",
        "color: inherit",
        "color: #2196F3; font-style: italic",
      );
      console.log(
        "%cnudge.clear()%c - Clear all nudges from the debugger - example: %cnudge.clear()",
        "color: #9C27B0; font-weight: bold",
        "color: inherit",
      );
      console.log("\n");
    },
    add: (key: string) => {
      const nudges = getDebugNudgeKeys();
      if (!nudges.includes(key)) {
        nudges.push(key);
        localStorage.setItem(DEBUG_NUDGE_KEY, JSON.stringify(nudges));
        console.log(`Added nudge to debugger: ${key}`);
      }
    },
    remove: (key: string) => {
      const nudges = getDebugNudgeKeys();
      const index = nudges.indexOf(key);
      if (index > -1) {
        nudges.splice(index, 1);
        localStorage.setItem(DEBUG_NUDGE_KEY, JSON.stringify(nudges));
        console.log(`Removed nudge from debugger: ${key}`);
      }
    },
    list: () => {
      const nudges = getDebugNudgeKeys();
      console.log("Current nudges in debugger:", nudges);
      return nudges;
    },
    clear: () => {
      localStorage.removeItem(DEBUG_NUDGE_KEY);
      console.log("Cleared all nudges from debugger");
    },
  };
}

export type Nudge = {
  key: string;
  title: string;
  description?: string;
  imageUrl?: string;
  videoUrl?: string;
  ctaText?: string;
  ctaAction?: string;
  ctaType?: string;
  priority: number;
  callToAction?: string;
};

export type NudgeInteractionType = "click" | "dismiss" | "impression";

const nudgeApi = axios.create({
  baseURL: `${fullAPIURL}/v2/personalization`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

nudgeApi.interceptors.request.use(requestAccessTokenInterceptor);
nudgeApi.interceptors.request.use(refreshAccessTokenInterceptor);
nudgeApi.interceptors.response.use(responseStatusInterceptor);

/**
 * Get a specific nudge by key
 * @param key The unique key for the nudge
 * @returns Promise with the nudge or null if not found
 */
export async function getNudgeByKey(key: string): Promise<Nudge | null> {
  const debugNudges = getDebugNudgeKeys();
  const params = new URLSearchParams({ key });

  if (debugNudges.includes(key)) {
    params.append("forceOverride", "true");
  }

  const response = await nudgeApi.get(`/nudges?${params.toString()}`);
  if (!isProd()) {
    if (response.data.debug) {
      console.log("\n");
      console.warn(
        "%c🤖 Nudge Evaluation Analysis 🤖",
        "color: #FF9800; font-size: 14px; font-weight: bold; text-decoration: underline;",
      );
      console.log(
        "Copy and paste the prompt and debug data below into your favorite LLM assistant to get a detailed explanation of why this nudge was or was not shown to the user.",
      );
      console.log("\n");
      console.log("\n");
      console.log(
        "%cPrompt:%c\nYou are a helpful assistant meant to explain the logic behind a nudge evaluation. This is a debug response for a rule evaluation using json-logic-js. Here is the context and the rule. Explain why this nudge was or was not shown to the user.",
        "color: #4CAF50; font-weight: bold",
        "color: inherit",
      );
      console.log(
        "%cDebug Data:%c\n" + JSON.stringify(response.data.debug, null, 2),
        "color: #2196F3; font-weight: bold",
        "color: inherit",
      );
      console.log("\n");
    }
  }

  const backendNudge = response.data.nudge;
  if (!backendNudge) {
    return null;
  }

  const mappedNudge: Nudge = {
    key: backendNudge.nudgeKey,
    title: backendNudge.title,
    description: backendNudge.description,
    imageUrl: backendNudge.imageUrl,
    videoUrl: backendNudge.videoUrl,
    ctaText: backendNudge.ctaText,
    ctaAction: backendNudge.ctaAction,
    ctaType: backendNudge.ctaType,
    priority: backendNudge.priority || 0,
  };

  return mappedNudge;
}

/**
 * Track a nudge interaction (click, dismiss, impression)
 * @param nudgeKey The unique key for the nudge
 * @param type The type of interaction
 */
export async function trackNudgeInteraction(
  nudgeKey: string,
  type: NudgeInteractionType,
): Promise<void> {
  await trackNudgeInteractionsBatch([{ nudgeKey, type }]);
}

/**
 * Track multiple nudge interactions in batch
 * @param interactions Array of interactions to create
 */
export async function trackNudgeInteractionsBatch(
  interactions: Array<{ nudgeKey: string; type: NudgeInteractionType }>,
): Promise<void> {
  const response = await nudgeApi.post("/nudges/interactions", {
    interactions,
  });

  if (response.status !== 204) {
    console.warn(
      `Failed to track nudge interactions batch: ${response.status}`,
    );
    throw new Error(`Batch tracking failed with status ${response.status}`);
  }
}

/**
 * Dismiss a nudge for the current user
 * @param key The unique key for the nudge to dismiss
 */
export async function dismissNudge(key: string): Promise<void> {
  await trackNudgeInteractionsBatch([{ nudgeKey: key, type: "dismiss" }]);
}

/**
 * Get nudges for a specific location
 * @param location The display location to filter nudges (e.g., "dashboard.sidebar")
 * @returns Promise with array of nudges for the location
 */
export async function getNudgesByLocation(location: string): Promise<Nudge[]> {
  const debugNudges = getDebugNudgeKeys();
  const params = new URLSearchParams({ location });

  // Add debug override if any nudges are in debug mode
  if (debugNudges.length > 0) {
    params.append("forceOverride", "true");
  }

  const response = await nudgeApi.get(`/nudges/location?${params.toString()}`);

  if (!isProd() && response.data.nudges) {
    const debugNudges = response.data.nudges.filter((n: any) => n.debug);
    if (debugNudges.length > 0) {
      console.log("\n");
      console.warn(
        "%c🤖 Location Nudges Evaluation Analysis 🤖",
        "color: #FF9800; font-size: 14px; font-weight: bold; text-decoration: underline;",
      );
      console.log(
        `Found ${response.data.nudges.length} nudges for location "${location}"`,
      );
      debugNudges.forEach((nudgeData: any, index: number) => {
        console.log(
          `\n--- Nudge ${index + 1}: ${nudgeData.nudge?.nudgeKey} ---`,
        );
        console.log(
          "%cDebug Data:%c\n" + JSON.stringify(nudgeData.debug, null, 2),
          "color: #2196F3; font-weight: bold",
          "color: inherit",
        );
      });
      console.log("\n");
    }
  }

  const nudges = response.data.nudges || [];

  return nudges.map((nudgeData: any) => {
    const backendNudge = nudgeData.nudge || nudgeData;

    const mappedNudge: Nudge = {
      key: backendNudge.nudgeKey,
      title: backendNudge.title,
      description: backendNudge.description,
      imageUrl: backendNudge.imageUrl,
      videoUrl: backendNudge.videoUrl,
      ctaText: backendNudge.ctaText,
      ctaAction: backendNudge.ctaAction,
      ctaType: backendNudge.ctaType,
      priority: backendNudge.priority || 0,
    };

    return mappedNudge;
  });
}
